import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Loader2, Bell, ClipboardCheck, RefreshCw, Archive, Users, ArrowLeft } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";

/**
 * POS Server tab — order-level view of today's tickets grouped by their
 * service state. Polls /pos/service-orders every 4 s so multiple tablets
 * stay in sync collaboratively (per user choice: everyone sees everything).
 *
 * Columns:
 *   - to_serve         : bar has all lines ready → server should bring them
 *   - served           : brought to the table, waiting for the server to close
 *   - in_prep          : being made at the bar (read-only reference)
 */
const euro = (n) => `${Number(n || 0).toFixed(2)} €`;

function Card({ order, actions }) {
  const short = order.short_id || order.id?.slice(0, 8)?.toUpperCase();
  return (
    <div className="rounded-2xl border border-border bg-card p-4 space-y-3" data-testid={`service-card-${order.id}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">#{short}</div>
          <div className="font-display text-lg leading-tight">
            {order.table_label || (order.table_num ? `Table ${order.table_num}` : "Comptoir")}
            {order.customer_first_name && <span className="text-muted-foreground text-sm ml-2 font-body font-normal">— {order.customer_first_name}</span>}
          </div>
        </div>
        <div className="text-right">
          <div className="text-primary font-display text-lg">{euro(order.total)}</div>
        </div>
      </div>

      {order.line_prep_summary?.total_units > 0 && (
        <div className="flex items-center gap-1 text-[11px]">
          {order.line_prep_summary.pending > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-slate-500/20 text-slate-300">
              {order.line_prep_summary.pending} en attente
            </span>
          )}
          {order.line_prep_summary.preparing > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300">
              {order.line_prep_summary.preparing} en prépa
            </span>
          )}
          {order.line_prep_summary.ready > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300">
              {order.line_prep_summary.ready} prête·s
            </span>
          )}
          {order.line_prep_summary.served > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-primary/20 text-primary">
              {order.line_prep_summary.served} servie·s
            </span>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-2">{actions}</div>
    </div>
  );
}

export default function POSService() {
  const [feed, setFeed] = useState({ to_serve: [], served: [], in_prep: [] });
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(null); // id of the order currently being actioned
  const [archiving, setArchiving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/pos/service-orders");
      setFeed(data);
    } catch (err) {
      const st = err.response?.status;
      if (st === 401) { window.location.href = "/pos"; return; }
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur chargement");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, []);

  const doAction = async (orderId, path, label) => {
    setPending(orderId);
    try {
      await api.post(`/pos/orders/${orderId}/${path}`);
      toast.success(label);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    } finally { setPending(null); }
  };

  const archiveToday = async () => {
    if (!window.confirm("Archiver toutes les commandes clôturées / servies du jour ?")) return;
    setArchiving(true);
    try {
      const { data } = await api.post("/pos/orders/archive-today");
      toast.success(`${data.archived} commande(s) archivée(s)`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    } finally { setArchiving(false); }
  };

  const totalActive = useMemo(
    () => feed.to_serve.length + feed.served.length + feed.in_prep.length,
    [feed],
  );

  return (
    <div className="min-h-[calc(100vh-4rem)] p-4 sm:p-6" data-testid="pos-service-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <a href="/pos" className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1">
                <ArrowLeft className="w-3 h-3" /> Tableau POS
              </a>
            </div>
            <h1 className="font-display text-3xl mt-1">Service · Salle</h1>
            <p className="text-sm text-muted-foreground">
              {totalActive === 0 ? "Aucune commande active." : `${totalActive} commande(s) en cours aujourd'hui`}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="pos-service-refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Rafraîchir
            </Button>
            <Button variant="outline" size="sm" onClick={archiveToday} disabled={archiving} data-testid="pos-service-archive-btn">
              <Archive className="w-4 h-4 mr-2" /> Archiver le jour
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground py-12 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {/* Ready to serve */}
            <section data-testid="col-to-serve">
              <div className="flex items-center gap-2 mb-3">
                <Bell className="w-4 h-4 text-emerald-400" />
                <h2 className="font-display text-lg">À servir</h2>
                <span className="text-xs text-muted-foreground">({feed.to_serve.length})</span>
              </div>
              <div className="space-y-3">
                {feed.to_serve.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-border/60 p-4 text-xs text-muted-foreground text-center">
                    Rien à servir pour le moment.
                  </div>
                ) : feed.to_serve.map((o) => (
                  <Card key={o.id} order={o} actions={
                    <Button
                      size="sm"
                      onClick={() => doAction(o.id, "mark-served", "Marqué servi")}
                      disabled={pending === o.id}
                      className="rounded-full"
                      data-testid={`mark-served-${o.id}`}
                    >
                      {pending === o.id ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : <Bell className="w-3 h-3 mr-2" />}
                      Marquer servi
                    </Button>
                  } />
                ))}
              </div>
            </section>

            {/* Served, awaiting closure */}
            <section data-testid="col-served">
              <div className="flex items-center gap-2 mb-3">
                <ClipboardCheck className="w-4 h-4 text-primary" />
                <h2 className="font-display text-lg">À clôturer</h2>
                <span className="text-xs text-muted-foreground">({feed.served.length})</span>
              </div>
              <div className="space-y-3">
                {feed.served.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-border/60 p-4 text-xs text-muted-foreground text-center">
                    Aucune commande servie en attente.
                  </div>
                ) : feed.served.map((o) => (
                  <Card key={o.id} order={o} actions={
                    <>
                      <Button
                        size="sm" variant="outline"
                        onClick={() => doAction(o.id, "service-close", "Ticket clôturé")}
                        disabled={pending === o.id}
                        className="rounded-full"
                        data-testid={`close-${o.id}`}
                      >
                        {pending === o.id ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : <ClipboardCheck className="w-3 h-3 mr-2" />}
                        Clôturer
                      </Button>
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => doAction(o.id, "archive", "Archivé")}
                        disabled={pending === o.id}
                        className="rounded-full text-muted-foreground"
                        data-testid={`archive-${o.id}`}
                      >
                        <Archive className="w-3 h-3 mr-1" /> Archiver
                      </Button>
                    </>
                  } />
                ))}
              </div>
            </section>

            {/* In prep (read-only) */}
            <section data-testid="col-in-prep">
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-4 h-4 text-blue-400" />
                <h2 className="font-display text-lg">En cours (bar)</h2>
                <span className="text-xs text-muted-foreground">({feed.in_prep.length})</span>
              </div>
              <div className="space-y-3">
                {feed.in_prep.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-border/60 p-4 text-xs text-muted-foreground text-center">
                    Aucune commande en préparation.
                  </div>
                ) : feed.in_prep.map((o) => (
                  <Card key={o.id} order={o} actions={
                    <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Préparation en cours</span>
                  } />
                ))}
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
